import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEBT_COLORS = ['#e8a838', '#d4603a', '#cd7f32', '#c0c0c0', '#a855f7'];

export function TimelineScreen() {
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState(86);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('hybrid');

  const fetchTimeline = useCallback(async (ex, m) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/strategy/timeline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extra_monthly: ex, method: m }),
        credentials: 'include'
      });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTimeline(extra, method); }, [fetchTimeline, extra, method]);

  const handleExtraChange = (val) => {
    setExtra(val);
  };

  if (loading && !data) return (
    <div data-testid="timeline-loading" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={0.4} />
      <div className="pulse-a" className="dettio-screen__inner">{Ic.flame(32, GOLD)}</div>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', marginTop: 12, position: 'relative', zIndex: 1 }}>Calculating your freedom timeline...</Bd>
    </div>
  );

  if (!data || !data.timeline?.length) return (
    <div style={{ padding: '40px 28px', textAlign: 'center' }}>
      <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>No debt data available</Bd>
    </div>
  );

  const names = data.debt_names || [];
  const chartData = data.timeline.filter((_, i) => i % Math.max(1, Math.floor(data.timeline.length / 30)) === 0 || i === data.timeline.length - 1);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--surface)', border: `1px solid ${FAINT}`, padding: '10px 14px', minWidth: 140 }}>
        <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 6 }}>MONTH {label}</Mo>
        {payload.filter(p => p.value > 0).map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 6, height: 6, background: p.color }} />
              <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{p.dataKey}</Mo>
            </div>
            <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--light)' }}>${p.value.toLocaleString()}</Mo>
          </div>
        ))}
        <div style={{ borderTop: `1px solid ${FAINT}`, marginTop: 4, paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)' }}>Total</Mo>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)' }}>${payload.reduce((s, p) => s + (p.value || 0), 0).toLocaleString()}</Mo>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="timeline-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        {/* Hero stats */}
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>DEBT FREE IN</Mo>
          <Dp style={{ fontSize: 42, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2 }}>{data.total_months} mo</Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block' }}>{data.free_by}</Bd>
        </div>

        {/* Comparison stats */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          <div style={{ flex: 1, padding: '12px 8px', background: 'var(--faint)', textAlign: 'center' }}>
            <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>MONTHS SAVED</Mo>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>{data.months_saved}</Mo>
          </div>
          <div style={{ flex: 1, padding: '12px 8px', background: 'var(--faint)', textAlign: 'center' }}>
            <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>INTEREST SAVED</Mo>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>${data.interest_saved?.toLocaleString()}</Mo>
          </div>
          <div style={{ flex: 1, padding: '12px 8px', background: 'var(--faint)', textAlign: 'center' }}>
            <Mo style={{ fontSize: 7, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 3 }}>EXTRA/MO</Mo>
            <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)' }}>${extra}</Mo>
          </div>
        </div>

        {/* Chart */}
        <Label>PAYOFF PROJECTION</Label>
        <div style={{ height: 220, marginBottom: 20, marginLeft: -10 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
              <defs>
                {names.map((n, i) => (
                  <linearGradient key={n} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={DEBT_COLORS[i % DEBT_COLORS.length]} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={DEBT_COLORS[i % DEBT_COLORS.length]} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: GHOST, fontFamily: MONO }} tickLine={false} axisLine={{ stroke: FAINT }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: GHOST, fontFamily: MONO }} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: GHOST, strokeDasharray: '3 3' }} />
              {names.map((n, i) => (
                <Area key={n} type="monotone" dataKey={n} stackId="1" stroke={DEBT_COLORS[i % DEBT_COLORS.length]} fill={`url(#grad-${i})`} strokeWidth={1.5} dot={false} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Extra Payment Slider */}
        <Label>EXTRA MONTHLY PAYMENT</Label>
        <div style={{ marginBottom: 8 }}>
          <input type="range" min={0} max={500} step={10} value={extra} onChange={e => handleExtraChange(+e.target.value)}
            data-testid="timeline-extra-slider" style={{ width: '100%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>$0</Mo>
            <Mo style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>${extra}/mo</Mo>
            <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>$500</Mo>
          </div>
        </div>

        {/* Strategy selector */}
        <Label>STRATEGY</Label>
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {[{ id: 'snowball', label: 'Snowball', desc: 'Lowest balance first' }, { id: 'avalanche', label: 'Avalanche', desc: 'Highest APR first' }, { id: 'hybrid', label: 'Hybrid', desc: 'Smart mix' }].map(s => (
            <button key={s.id} onClick={() => setMethod(s.id)} data-testid={`strategy-${s.id}`} style={{
              flex: 1, padding: '10px 6px', background: method === s.id ? `${GOLD}12` : FAINT,
              border: `1px solid ${method === s.id ? GOLD : 'transparent'}`, cursor: 'pointer', textAlign: 'center'
            }}>
              <Mo style={{ fontSize: 9, fontWeight: 700, color: method === s.id ? GOLD : MUTE, letterSpacing: 0.5, display: 'block' }}>{s.label.toUpperCase()}</Mo>
              <Mo style={{ fontSize: 7, color: 'var(--ghost)', marginTop: 2, display: 'block' }}>{s.desc}</Mo>
            </button>
          ))}
        </div>

        {/* Debt payoff dates */}
        <Label>PAYOFF DATES</Label>
        {names.map((n, i) => {
          const date = data.payoff_dates?.[n] || '—';
          const months = Object.entries(data.payoff_dates || {}).find(([k]) => k === n)?.[1];
          return (
            <div key={n} data-testid={`payoff-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${FAINT}` }}>
              <div style={{ width: 8, height: 8, background: DEBT_COLORS[i % DEBT_COLORS.length] }} />
              <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)', flex: 1 }}>{n}</Bd>
              <Mo style={{ fontSize: 12, fontWeight: 700, color: DEBT_COLORS[i % DEBT_COLORS.length] }}>{date}</Mo>
            </div>
          );
        })}

        {/* Insight */}
        <div style={{ marginTop: 20, padding: 14, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)', letterSpacing: 0.5, lineHeight: 1.7 }}>
            {extra > 0
              ? `Adding $${extra}/mo saves you ${data.months_saved} months and $${data.interest_saved?.toLocaleString()} in interest compared to minimums only (${data.min_only_months} months).`
              : `Paying only minimums would take ${data.min_only_months} months and cost $${data.min_only_interest?.toLocaleString()} in interest. Add extra to accelerate!`}
          </Mo>
        </div>
      </div>
    </div>
  );
}
